import { CURATOR_INVITE_TTL_DAYS, fmtDate, fmtRecent } from '@remote-medium/shared';
import { unwrap, useApi } from '@remote-medium/ui';
import { useState } from 'react';

import { admin } from '../../lib/client.ts';
import { useCurrentCurator } from '../../lib/current-curator.tsx';
import { CuratorStatusChip } from '../../lib/status-chip.tsx';
import {
  ConfirmButton,
  DataTable,
  EmptyRow,
  None,
  Notice,
  Page,
  PageHeader,
  StatusRow,
  tableReady,
} from '../../lib/ui.tsx';
import { InviteCurator } from './components/InviteCurator.tsx';

export function CuratorsPage() {
  const me = useCurrentCurator();
  const { data, error, loading, reload } = useApi(() => unwrap(admin.curators.$get()), []);
  const [notice, setNotice] = useState<string | null>(null);
  const ready = tableReady({ data, loading, error });
  const rows = data ?? [];

  // Every action reloads the list; the notice says what just happened.
  async function act(run: () => Promise<unknown>, done: string) {
    await run();
    setNotice(done);
    reload();
  }
  const param = (id: string) => ({ param: { id } });

  return (
    <Page className="gap-6">
      <PageHeader
        title="Curators"
        description={`Everyone who can sign in to this dashboard. Invite links work for ${CURATOR_INVITE_TTL_DAYS} days. Curators who accepted are deactivated, never removed, so their name stays on past decisions.`}
        actions={
          <InviteCurator
            onInvite={(json) =>
              act(
                () => unwrap(admin.curators.invites.$post({ json })),
                `Invite sent to ${json.email}.`,
              )
            }
          />
        }
      />

      {notice && <Notice>{notice}</Notice>}

      <DataTable>
        <thead>
          <tr>
            <th>Curator</th>
            <th>Status</th>
            <th>Invited</th>
            <th>Last sign-in</th>
            <th>
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <StatusRow colSpan={5} loading={loading} error={error} />
          {ready && rows.length === 0 && <EmptyRow colSpan={5}>No curators yet.</EmptyRow>}
          {ready &&
            rows.map((c) => {
              const isMe = c.id === me?.id;
              const pending = c.status === 'invited' || c.status === 'expired';
              return (
                <tr key={c.id} className="hover:bg-muted/50">
                  <td className="max-w-72">
                    <div className="font-semibold text-foreground">
                      {c.fullName}
                      {isMe && <span className="font-normal text-muted-foreground"> (you)</span>}
                    </div>
                    <div className="truncate text-muted-foreground">{c.email}</div>
                  </td>
                  <td>
                    <div className="flex flex-col items-start gap-1">
                      <CuratorStatusChip status={c.status} />
                      {c.status === 'invited' && c.inviteExpiresAt && (
                        <span className="text-xs text-muted-foreground">
                          Link expires {fmtDate(c.inviteExpiresAt)}
                        </span>
                      )}
                      {c.status === 'active' && c.reactivatedAt && (
                        <span className="text-xs text-muted-foreground">
                          Reactivated{c.reactivatedByName && ` by ${c.reactivatedByName}`} ·{' '}
                          {fmtDate(c.reactivatedAt)}
                        </span>
                      )}
                      {c.status === 'deactivated' && c.deactivatedAt && (
                        <span className="text-xs text-muted-foreground">
                          {[c.deactivatedByName, fmtDate(c.deactivatedAt)]
                            .filter(Boolean)
                            .join(' · ')}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="whitespace-nowrap">
                    {c.invitedAt ? (
                      <>
                        <div>{c.invitedByName ?? <None>Unknown</None>}</div>
                        <div className="text-muted-foreground tabular-nums">
                          {fmtRecent(c.invitedAt)}
                        </div>
                      </>
                    ) : (
                      <None>Set up by script</None>
                    )}
                  </td>
                  <td className="whitespace-nowrap text-muted-foreground tabular-nums">
                    {c.lastSignInAt ? fmtRecent(c.lastSignInAt) : <None>Never</None>}
                  </td>
                  <td>
                    <div className="flex justify-end gap-2">
                      {pending && (
                        <>
                          <ConfirmButton
                            title={`Resend the invite to ${c.fullName}?`}
                            description={`A new link goes to ${c.email}, valid for ${CURATOR_INVITE_TTL_DAYS} days. Earlier links stop working.`}
                            confirmLabel="Resend invite"
                            onConfirm={() =>
                              act(
                                () =>
                                  unwrap(admin.curators[':id'].invite.resend.$post(param(c.id))),
                                `Invite sent again to ${c.email}.`,
                              )
                            }
                          >
                            Resend
                          </ConfirmButton>
                          <ConfirmButton
                            title={`Cancel the invite to ${c.fullName}?`}
                            description="The link stops working and they disappear from this list. You can invite them again later."
                            confirmLabel="Cancel invite"
                            cancelLabel="Keep invite"
                            destructive
                            variant="ghost"
                            onConfirm={() =>
                              act(
                                () => unwrap(admin.curators[':id'].invite.$delete(param(c.id))),
                                `Invite to ${c.email} cancelled.`,
                              )
                            }
                          >
                            Cancel invite
                          </ConfirmButton>
                        </>
                      )}
                      {c.status === 'active' && !isMe && (
                        <ConfirmButton
                          title={`Deactivate ${c.fullName}?`}
                          description="They are signed out everywhere and can't open this dashboard until a curator reactivates them. Their past decisions keep their name."
                          confirmLabel="Deactivate"
                          destructive
                          onConfirm={() =>
                            act(
                              () => unwrap(admin.curators[':id'].deactivate.$post(param(c.id))),
                              `${c.fullName} is deactivated.`,
                            )
                          }
                        >
                          Deactivate
                        </ConfirmButton>
                      )}
                      {c.status === 'deactivated' && (
                        <ConfirmButton
                          title={`Reactivate ${c.fullName}?`}
                          description="They can sign in with their existing password again."
                          confirmLabel="Reactivate"
                          onConfirm={() =>
                            act(
                              () => unwrap(admin.curators[':id'].reactivate.$post(param(c.id))),
                              `${c.fullName} is active again.`,
                            )
                          }
                        >
                          Reactivate
                        </ConfirmButton>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
        </tbody>
      </DataTable>
    </Page>
  );
}
